import { Component, ViewChild, ElementRef } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Account } from 'app/model/entities/entity-model';
import { DialogBaseComponent, DialogParams } from 'app/shared/dialog-base.component';
import { DomainService } from 'app/shared/domain.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { environment } from 'environments/environment';
import { UtilFns } from 'app/shared/util-fns';

interface QboCustomerLinkDialogParams extends DialogParams {
  account: Account;
}

interface QBCustomerSearchResult {
  Id: string;
  DisplayName: string;
  CompanyName: string;
  LinkedUniformaxAccountId?: number | null;
  LinkedUniformaxAccountName?: string | null;
}

@Component({
  templateUrl: './qbo-customer-link-dialog.component.html',
  standalone: false,
})
export class QboCustomerLinkDialogComponent extends DialogBaseComponent<QboCustomerLinkDialogParams> {
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  account: Account;

  searchQuery = '';
  searchResults: QBCustomerSearchResult[] = [];
  isSearching = false;
  searchError: string | null = null;
  hasSearched = false;

  createDisplayName = '';
  createCompanyName = '';
  isCreating = false;
  createError: string | null = null;

  constructor(
    public config: DynamicDialogConfig<any>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService,
    private http: HttpClient,
  ) {
    super(config, dialogRef, domainService);
    Object.assign(this.config, {
      header: 'Set up QuickBooks customer',
      width: '900px',
      height: '640px',
    });
    this.prepare();
  }

  private async prepare() {
    this.account = this.params.account;
    this.searchQuery = this.account.accountName ?? '';
    this.createDisplayName = this.account.accountName ?? '';
    this.createCompanyName = this.account.accountName ?? '';
    this.isPageReady = true;
    await UtilFns.wait(1);
    this.searchInput?.nativeElement?.focus();
    // Auto-run initial search on open using the account name.
    if (this.searchQuery.length >= 3) {
      this.runSearch();
    }
  }

  async runSearch() {
    this.searchError = null;
    const q = this.searchQuery?.trim() ?? '';
    if (q.length < 3) {
      this.searchError = 'Type at least 3 characters to search.';
      this.searchResults = [];
      return;
    }
    this.isSearching = true;
    this.hasSearched = true;
    try {
      const url = environment.apiRoot + 'api/quickbooks/SearchQBCustomers?query=' + encodeURIComponent(q);
      this.searchResults = await firstValueFrom(this.http.get<QBCustomerSearchResult[]>(url));
    } catch (err: any) {
      this.searchError = err?.error?.Message || err?.message || 'Search failed.';
      this.searchResults = [];
    } finally {
      this.isSearching = false;
    }
  }

  isRowLinkable(row: QBCustomerSearchResult): boolean {
    return !row.LinkedUniformaxAccountId || row.LinkedUniformaxAccountId === this.account.id;
  }

  rowLinkedNote(row: QBCustomerSearchResult): string {
    if (!row.LinkedUniformaxAccountId) return '';
    if (row.LinkedUniformaxAccountId === this.account.id) return 'Already linked to this account';
    return `Linked to ${row.LinkedUniformaxAccountName ?? 'Account ' + row.LinkedUniformaxAccountId}`;
  }

  async linkTo(row: QBCustomerSearchResult) {
    if (!this.isRowLinkable(row)) return;
    this.account.gLAccountRef = row.Id;
    await this.dbSaveService.saveChanges();
    this.domainService.toastService.success(`Linked to QBO customer ${row.DisplayName} (${row.Id}).`);
    this.dialogRef.close(true);
  }

  async createNew() {
    this.createError = null;
    if (!this.createDisplayName?.trim()) {
      this.createError = 'Display Name is required.';
      return;
    }
    if (!this.createCompanyName?.trim()) {
      this.createError = 'Company Name is required.';
      return;
    }
    this.isCreating = true;
    try {
      const url = environment.apiRoot + 'api/quickbooks/CreateQBCustomerForAccount';
      const body = {
        AccountId: this.account.id,
        DisplayName: this.createDisplayName.trim(),
        CompanyName: this.createCompanyName.trim(),
      };
      const result = await firstValueFrom(this.http.post<{ GLAccountRef: string }>(url, body));
      // The server persisted GLAccountRef; refresh the local entity so the caller sees the change.
      this.account.gLAccountRef = result.GLAccountRef;
      // Clear any tracked changes so the outer dialog doesn't think we still need to save.
      this.account.entityAspect.setUnchanged();
      this.domainService.toastService.success(`Created QBO customer ${result.GLAccountRef}.`);
      this.dialogRef.close(true);
    } catch (err: any) {
      this.createError = err?.error?.Message || err?.error || err?.message || 'Create failed.';
    } finally {
      this.isCreating = false;
    }
  }

  cancel() {
    this.dialogRef.close(false);
  }
}
